/**
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business SNSコンテンツ生成画面のテーマ・タイトル生成・画像生成・ダウンロード記録・キャプション生成（実API）と、生成・確認画面間で確定行をReact stateだけで受け渡すフック集
 * @graph-connects snsContentGeneratorApi [calls] テーマ・タイトル・画像プロンプト・画像生成API、ダウンロード記録、キャプション生成の呼び出し
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { logError } from '@cortex/faro';
import { snsContentGeneratorApi } from '../lib/api.js';

/** 生成された1件のテーマ・タイトル。TikTok投稿企画のtheme/title構造に準拠する */
export interface GeneratedThemeTitle {
  /** 投稿テーマ */
  theme: string;
  /** 投稿タイトル */
  title: string;
}

/** 実行確定後の1件のテーマ・タイトル。 */
export interface ConfirmedThemeTitle {
  /** 行の識別子。編集画面の行idをそのまま引き継ぐ */
  id: string;
  /** 投稿テーマ */
  theme: string;
  /** 投稿タイトル */
  title: string;
}

/** {@link useGenerateThemeTitles} に渡すミューテーション引数。 @graph-connects none */
export interface GenerateThemeTitlesInput {
  /** 生成対象のアカウントID */
  accountId: string;
  /** 除外するテーマ名の一覧（編集中リストに既にあるテーマ） */
  excludeThemes: string[];
}

/**
 * テーマ・タイトル生成ミューテーション。
 *
 * `generateThemes`（コンセプト生成 LLM 1回）でテーマ候補を取得し、
 * テーマごとに `generateTitle`（タイトル生成 LLM 1回）を並列に呼んでタイトルを補う。
 * この2段に分けているのは admin-api の Cloud Run timeout（180s）を超えないため
 * （方針書5章。テーマ複数件のタイトル生成を1リクエストにまとめると LLM 呼び出し回数が
 * 増えすぎて超える）。
 *
 * `generateTitle` はテーマごとに独立した課金済みの LLM 呼び出しのため、`Promise.allSettled`
 * で個別に成否を扱い、一部のテーマで失敗しても成功した分は結果として返す。全テーマで
 * 失敗した場合のみエラーにする。
 *
 * @graph-connects snsContentGeneratorApi [calls] generateThemes → generateTitle の順に呼び出す
 * @graph-connects logError [calls] タイトル生成に失敗したテーマをFaroへ記録
 */
export function useGenerateThemeTitles() {
  return useMutation({
    mutationFn: async ({
      accountId,
      excludeThemes,
    }: GenerateThemeTitlesInput): Promise<GeneratedThemeTitle[]> => {
      const { themes } = await snsContentGeneratorApi.generateThemes(excludeThemes, accountId);
      const results = await Promise.allSettled(
        themes.map(async (concept) => {
          const { title } = await snsContentGeneratorApi.generateTitle(concept.theme, accountId);
          return { theme: concept.theme, title };
        }),
      );

      const succeeded: GeneratedThemeTitle[] = [];
      for (const result of results) {
        if (result.status === 'fulfilled') {
          succeeded.push(result.value);
        } else {
          logError(
            result.reason instanceof Error ? result.reason : new Error(String(result.reason)),
            {
              event: 'sns_content_generator_generate_title_failed',
            },
          );
        }
      }

      if (succeeded.length === 0 && themes.length > 0) {
        throw new Error('All theme title generations failed');
      }
      return succeeded;
    },
  });
}

/**
 * 確定済み（実行済み）テーマ・タイトルを React の state だけで保持するフック。
 *
 * 行ごとの個別「実行」ボタン、および一括「実行」ボタンの両方から
 * `appendConfirmedThemeTitles` を呼んで末尾に積み上げていく。同じテーマは
 * 二重に積まれないよう追記時に除外する。永続化はせず、リロードすれば空に戻る。
 *
 * @graph-connects none
 */
export function useConfirmedThemeTitles() {
  const [confirmedThemeTitles, setConfirmedThemeTitles] = useState<ConfirmedThemeTitle[]>([]);

  const appendConfirmedThemeTitles = useCallback((items: ConfirmedThemeTitle[]) => {
    setConfirmedThemeTitles((prev) => {
      const seenThemes = new Set(prev.map((item) => item.theme));
      const additions = items.filter((item) => {
        if (seenThemes.has(item.theme)) return false;
        seenThemes.add(item.theme);
        return true;
      });
      if (additions.length === 0) return prev;
      return [...prev, ...additions];
    });
  }, []);

  /** 画像生成対象から1件だけ取り除く。 */
  const removeConfirmedThemeTitle = useCallback((id: string) => {
    setConfirmedThemeTitles((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return { confirmedThemeTitles, appendConfirmedThemeTitles, removeConfirmedThemeTitle } as const;
}

/** 画像生成枚数として選べる最小値 @graph-connects none */
export const MIN_IMAGE_PAGE_COUNT = 1;

/** 画像生成枚数として選べる最大値 @graph-connects none */
export const MAX_IMAGE_PAGE_COUNT = 10;

/** 生成された1ページ分の画像 */
export interface GeneratedImagePage {
  /** ページ番号表示（例: "1/5"） */
  pageNumber: string;
  /** GCS 原本バケットの V4 署名付きURL（別オリジン） */
  imageUrl: string;
}

/** 1件のテーマ・タイトルにつき生成された画像一式 */
export interface GeneratedContentImages {
  /** 確定済みテーマ・タイトルのidをそのまま引き継ぐ */
  id: string;
  /** Python 側の Firestore コンテンツドキュメントID。generateImage / recordContentDownload で使う */
  contentId: string;
  /** 投稿テーマ */
  theme: string;
  /** 投稿タイトル */
  title: string;
  /** 生成されたページ画像一覧（画像生成中は生成完了したページから順に増える） */
  pages: GeneratedImagePage[];
}

/** 画像生成APIの同時実行数の上限。OpenAI のレート制限と Python サービスのインスタンス占有を考慮する @graph-connects none */
const IMAGE_GENERATION_CONCURRENCY = 3;

/**
 * items を worker で処理する際、同時実行数を limit 件までに制限する。
 * ワーカーが例外を投げた場合はそのまま呼び出し元へ伝播する（Promise.all と同じ挙動）。
 *
 * @graph-connects none
 */
async function runWithConcurrencyLimit<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  let nextIndex = 0;
  /** @graph-connects none */
  async function runNext(): Promise<void> {
    const currentIndex = nextIndex;
    nextIndex += 1;
    if (currentIndex >= items.length) return;
    await worker(items[currentIndex]);
    await runNext();
  }
  const workerCount = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => runNext()));
}

/** ページ番号（例: "3/5"）の分子部分で昇順ソートするための比較関数 @graph-connects none */
function comparePageNumber(a: GeneratedImagePage, b: GeneratedImagePage): number {
  const parseNumerator = (pageNumber: string): number => {
    const numerator = pageNumber.split('/')[0];
    /* v8 ignore start -- pageNumber は必ず "N/M" 形式で split 結果が空になることはない */
    if (numerator === undefined) return 0;
    /* v8 ignore stop */
    return Number.parseInt(numerator, 10);
  };
  return parseNumerator(a.pageNumber) - parseNumerator(b.pageNumber);
}

/** {@link useGenerateContentImages} に渡すミューテーション引数 */
export interface GenerateContentImagesInput {
  /** 生成対象のアカウントID */
  accountId: string;
  /** 画像生成対象のテーマ・タイトル一覧 */
  items: ConfirmedThemeTitle[];
  /** テーマ・タイトル1件あたりの画像生成枚数（ユーザーが選択した値） */
  pageCount: number;
  /**
   * テーマ・タイトル1件ぶんの画像生成（`generatePagePrompts`呼び出し）を始める直前に呼ばれる。
   * この時点で theme/title は確定しているため、呼び出し元はこれを使ってキャプション生成を
   * 画像生成と同時に開始できる（画像生成の完了を待たない）。
   */
  onItemStart?: (item: ConfirmedThemeTitle) => void;
  /**
   * テーマ・タイトル1件ぶんの画像生成が進むたびに、その時点までの生成結果で呼ばれる
   * （画像生成の開始時は pages: [] で呼ばれ、ページが1枚生成されるごとに増えていく）。
   * 呼び出し元はこれを使って画面へ逐次反映する。
   */
  onItemUpdate?: (image: GeneratedContentImages) => void;
}

/**
 * 画像生成ミューテーション。テーマ・タイトル1件ごとに `generatePagePrompts`（画像プロンプト生成、
 * LLM 1回）を呼んでから、返ってきた各ページを `generateImage`（画像1枚）で並列生成する。
 * 並列数は {@link IMAGE_GENERATION_CONCURRENCY} に制限し、ページが1枚生成されるたびに
 * `onItemUpdate` で呼び出し元へ通知して逐次表示できるようにする。
 *
 * @graph-connects snsContentGeneratorApi [calls] generatePagePrompts → generateImage の順に呼び出す
 */
export function useGenerateContentImages() {
  return useMutation({
    mutationFn: async ({
      accountId,
      items,
      pageCount,
      onItemStart,
      onItemUpdate,
    }: GenerateContentImagesInput): Promise<GeneratedContentImages[]> => {
      const results: GeneratedContentImages[] = [];
      for (const item of items) {
        onItemStart?.(item);
        const { contentId, pages: pagePrompts } = await snsContentGeneratorApi.generatePagePrompts({
          theme: item.theme,
          title: item.title,
          pageCount,
          accountId,
        });

        const generatedPages: GeneratedImagePage[] = [];
        const emitUpdate = () => {
          onItemUpdate?.({
            id: item.id,
            contentId,
            theme: item.theme,
            title: item.title,
            pages: [...generatedPages].sort(comparePageNumber),
          });
        };
        emitUpdate();

        await runWithConcurrencyLimit(pagePrompts, IMAGE_GENERATION_CONCURRENCY, async (page) => {
          const { imageUrl } = await snsContentGeneratorApi.generateImage({
            contentId,
            pageNumber: page.pageNumber,
            accountId,
          });
          generatedPages.push({ pageNumber: page.pageNumber, imageUrl });
          emitUpdate();
        });

        results.push({
          id: item.id,
          contentId,
          theme: item.theme,
          title: item.title,
          pages: [...generatedPages].sort(comparePageNumber),
        });
      }
      return results;
    },
  });
}

/** {@link useRecordContentDownload} に渡すミューテーション引数。 */
export interface RecordContentDownloadInput {
  /** ダウンロードされたコンテンツが属するアカウントID */
  accountId: string;
  /** Python 側の Firestore コンテンツドキュメントID */
  contentId: string;
}

/**
 * 画像がダウンロードされたことを Python サービスへ記録するミューテーション。
 * 記録は次回以降のテーマ生成での除外対象を増やすための副次的な処理で、失敗してもダウンロード
 * 自体のUXを妨げてはならないため、呼び出し元は成否に関わらずダウンロード処理を継続すること。
 *
 * @graph-connects snsContentGeneratorApi [calls] recordContentDownload
 */
export function useRecordContentDownload() {
  return useMutation({
    mutationFn: ({ accountId, contentId }: RecordContentDownloadInput) =>
      snsContentGeneratorApi.recordContentDownload(contentId, accountId),
  });
}

/** 1件のテーマ・タイトルに対して生成されるハッシュタグ・投稿文。 */
export interface GeneratedCaption {
  /** 投稿に添えるハッシュタグ一覧 */
  hashtags: string[];
  /** 投稿文 */
  postText: string;
}

/** {@link useGenerateCaption} に渡すミューテーション引数。 */
export interface GenerateCaptionInput {
  /** 生成対象のアカウントID */
  accountId: string;
  /** 投稿テーマ */
  theme: string;
  /** 投稿タイトル */
  title: string;
}

/**
 * ハッシュタグ・投稿文生成ミューテーション。テーマ・タイトルごとに `generateCaption`
 * （キャプション生成 LLM 1回）を呼び出す。Python側の `/captions` は theme/title しか
 * 使わないため、画像生成（`generatePagePrompts` / `generateImage`）の完了を待つ必要がない。
 * 呼び出し元は画像生成と同時にこのミューテーションを開始してよい。
 *
 * @graph-connects snsContentGeneratorApi [calls] generateCaption
 */
export function useGenerateCaption() {
  return useMutation({
    mutationFn: (input: GenerateCaptionInput): Promise<GeneratedCaption> =>
      snsContentGeneratorApi.generateCaption(input),
  });
}

/** {@link useConnectTiktok} に渡すミューテーション引数。 */
export interface ConnectTiktokInput {
  /** 送信対象のアカウントID */
  accountId: string;
  /** 送信対象のPython側Firestoreコンテンツドキュメントid */
  contentId: string;
  /** TikTok下書きのtitle（該当テーマのtitleをそのまま使う。90文字制限はPython側で切る） */
  title: string;
  /** TikTok下書きのdescription。captionTexts（投稿文とハッシュタグを結合した編集後テキスト）をそのまま渡す */
  description: string;
}

/**
 * TikTok下書き送信ミューテーション。ハッシュタグ・説明文生成後、テーマ・タイトルごとに呼び出す。
 * 下書き（`post_mode: MEDIA_UPLOAD`）としてTikTokアプリの受信箱へ送るだけで、公開はされない。
 *
 * @graph-connects snsContentGeneratorApi [calls] tiktokCreateDraft
 */
export function useConnectTiktok() {
  return useMutation({
    mutationFn: (input: ConnectTiktokInput): Promise<{ publishId: string }> =>
      snsContentGeneratorApi.tiktokCreateDraft(input),
  });
}

/**
 * TikTok連携（OAuth）の認証URL発行ミューテーション。TikTokアカウントは組織で1つ共有のため、
 * 一度連携が完了すればアクセストークンはCloud Schedulerが1日1回リフレッシュし続ける
 * （運用上、複数人が繰り返し呼ぶ想定ではない）。
 *
 * @graph-connects snsContentGeneratorApi [calls] tiktokAuthUrl
 */
export function useTiktokAuthUrl() {
  return useMutation({
    mutationFn: (accountId: string): Promise<{ authorizeUrl: string }> =>
      snsContentGeneratorApi.tiktokAuthUrl(accountId),
  });
}
